/**
 * TonkSettingsPanel — admin control surface for Good Ol' Tonk Game rules.
 *
 * All fields patch `/api/tonk/admin/settings` with an audit-logged PUT.
 * Layout is grouped by concern (feature flags, auto-Tonk numbers, catch
 * rules, timers, table shape, bots, financial) so an operator can find a
 * specific knob without hunting.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layers, Save, RefreshCw, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NumberField = ({ label, value, onChange, hint, min = 0, max, testid }) => (
    <div>
        <Label className="text-xs text-white/70">{label}</Label>
        <Input
            type="number"
            value={value ?? ''}
            min={min}
            max={max}
            onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
            className="bg-black/40 border-white/10 text-white h-10 mt-1"
            data-testid={testid}
        />
        {hint && <p className="text-[10px] text-white/40 mt-1">{hint}</p>}
    </div>
);

const ToggleField = ({ label, value, onChange, hint, testid }) => (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-white/5 last:border-b-0">
        <div className="min-w-0">
            <p className="text-sm text-white font-medium">{label}</p>
            {hint && <p className="text-[11px] text-white/50 mt-0.5">{hint}</p>}
        </div>
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={testid} />
    </div>
);

const Section = ({ title, children }) => (
    <div className="rounded-xl border border-white/10 bg-[#0e0e0e] p-4 space-y-3">
        <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">{title}</p>
        {children}
    </div>
);

const TonkSettingsPanel = () => {
    const [flags, setFlags] = useState({});
    const [rules, setRules] = useState({});
    const [presets, setPresets] = useState({});
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState('');
    const [dirty, setDirty] = useState({});
    const [applyingPreset, setApplyingPreset] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data }, presetsResp] = await Promise.all([
                axios.get(`${API}/tonk/admin/settings`, { withCredentials: true }),
                axios.get(`${API}/tonk/admin/presets`, { withCredentials: true }),
            ]);
            setFlags(data.flags || {});
            setRules(data.rules || {});
            setPresets(presetsResp.data || {});
            setDirty({});
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Tonk settings');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const set = (key, val) => {
        setRules((r) => ({ ...r, [key]: val }));
        setDirty((d) => ({ ...d, [key]: val }));
    };
    const setFlag = (key, val) => {
        setFlags((f) => ({ ...f, [key]: val }));
        setDirty((d) => ({ ...d, [key]: val }));
    };

    const save = async () => {
        if (Object.keys(dirty).length === 0) {
            toast('Nothing to save', { description: 'Change a value first.' });
            return;
        }
        if (!reason.trim()) {
            toast.error('Add a short reason (audit-logged)');
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.put(
                `${API}/tonk/admin/settings`,
                { ...dirty, reason: reason.trim() },
                { withCredentials: true },
            );
            setFlags(data.flags || {});
            setRules(data.rules || {});
            setDirty({});
            setReason('');
            toast.success('Tonk settings updated', { description: `${Object.keys(dirty).length} field(s) saved` });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    const applyPreset = async (key) => {
        if (Object.keys(dirty).length > 0) {
            if (!window.confirm('You have unsaved changes — applying a preset will overwrite them. Continue?')) {
                return;
            }
        }
        setApplyingPreset(key);
        try {
            const { data } = await axios.post(
                `${API}/tonk/admin/apply-preset`,
                { preset: key },
                { withCredentials: true },
            );
            setFlags(data.flags || {});
            setRules(data.rules || {});
            setDirty({});
            setReason('');
            toast.success(`Preset applied: ${presets[key]?.label || key}`, {
                description: `${data.changed || 0} rule(s) updated`,
            });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to apply preset');
        } finally {
            setApplyingPreset(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <div className="spinner" />
            </div>
        );
    }

    const changeCount = Object.keys(dirty).length;

    return (
        <div className="space-y-4" data-testid="tonk-settings-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-[#FFD700]" />
                    <div>
                        <p className="text-lg font-black text-white">Good Ol&apos; Tonk Game</p>
                        <p className="text-xs text-white/50">All game rules — every change is audit-logged</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="ghost" onClick={load} disabled={busy} data-testid="tonk-settings-reload">
                        <RefreshCw className="w-4 h-4 mr-1" /> Reload
                    </Button>
                </div>
            </div>

            {/* Preset profiles — one-tap A/B testing */}
            {Object.keys(presets).length > 0 && (
                <div
                    className="rounded-xl border border-[#FFD700]/30 bg-gradient-to-br from-[#FFD700]/10 via-[#0a0a0a] to-[#0a0a0a] p-4"
                    data-testid="tonk-preset-strip"
                >
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-[#FFD700]" />
                        <p className="text-[10px] uppercase tracking-widest text-[#FFD700] font-bold">
                            Quick presets — one-tap A/B test
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {Object.entries(presets).map(([key, p]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => applyPreset(key)}
                                disabled={busy || applyingPreset !== null}
                                className="text-left rounded-lg border border-white/10 bg-black/40 hover:bg-white/[0.04] hover:border-[#FFD700]/50 p-3 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid={`tonk-preset-${key}`}
                            >
                                <p className="text-sm font-black text-white mb-1">
                                    {applyingPreset === key ? 'Applying…' : p.label}
                                </p>
                                <p className="text-[11px] text-white/60 leading-snug">{p.description}</p>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Feature flags */}
            <Section title="Feature flags">
                <ToggleField
                    label="Tonk enabled"
                    hint="Master switch. When off, players can't see or create tables."
                    value={flags.enabled}
                    onChange={(v) => setFlag('enabled', v)}
                    testid="tonk-flag-enabled"
                />
                <ToggleField
                    label="Real-money tables"
                    hint="Off = only practice tables can be created."
                    value={flags.real_money_enabled}
                    onChange={(v) => setFlag('real_money_enabled', v)}
                    testid="tonk-flag-real"
                />
                <ToggleField
                    label="Bots enabled globally"
                    hint="Off = tables never auto-fill; humans-only."
                    value={flags.bots_enabled}
                    onChange={(v) => setFlag('bots_enabled', v)}
                    testid="tonk-flag-bots"
                />
                <ToggleField
                    label="Spectators allowed"
                    hint="Off = only seated players can view a live table."
                    value={flags.spectators_enabled}
                    onChange={(v) => setFlag('spectators_enabled', v)}
                    testid="tonk-flag-spectators"
                />
            </Section>

            {/* Auto-Tonk numbers */}
            <Section title="Auto-Tonk (initial deal)">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <NumberField
                        label="High auto-Tonk totals (CSV)"
                        value={(rules.high_auto_tonk_totals || []).join(',')}
                        onChange={(v) => set('high_auto_tonk_totals', String(v || '').split(',').map((n) => Number(n.trim())).filter(Number.isFinite))}
                        hint="Comma-separated. Default: 49,50"
                        testid="tonk-high-totals"
                    />
                    <NumberField
                        label="Double-pay high total"
                        value={rules.double_high_total}
                        onChange={(v) => set('double_high_total', v)}
                        hint="Which high total pays 2×. Default 50."
                        testid="tonk-double-high"
                    />
                    <NumberField
                        label="Low auto-Tonk threshold"
                        value={rules.low_auto_tonk_threshold}
                        onChange={(v) => set('low_auto_tonk_threshold', v)}
                        hint="Deals ≤ this = auto-win. Default 12."
                        testid="tonk-low-threshold"
                    />
                    <NumberField
                        label="Low auto-Tonk multiplier"
                        value={rules.low_auto_tonk_multiplier}
                        onChange={(v) => set('low_auto_tonk_multiplier', v)}
                        hint="Pays this × pot from each opponent. Default 2."
                        testid="tonk-low-mult"
                    />
                </div>
                <ToggleField
                    label="Low auto-Tonk enabled"
                    hint="Turn off to disable the ≤12 auto-win entirely."
                    value={rules.low_auto_tonk_enabled}
                    onChange={(v) => set('low_auto_tonk_enabled', v)}
                    testid="tonk-low-enabled"
                />
            </Section>

            {/* Play rules */}
            <Section title="Play rules">
                <ToggleField
                    label="Ace-high runs"
                    hint="Off = ace only counts low (A-2-3). On = also allows Q-K-A runs."
                    value={rules.ace_high_runs}
                    onChange={(v) => set('ace_high_runs', v)}
                    testid="tonk-ace-high"
                />
                <ToggleField
                    label="Hit others' spreads"
                    hint="Hit don't hold — default ON."
                    value={rules.can_hit_others}
                    onChange={(v) => set('can_hit_others', v)}
                    testid="tonk-hit-others"
                />
                <ToggleField
                    label="Hit own spreads"
                    value={rules.can_hit_own}
                    onChange={(v) => set('can_hit_own', v)}
                    testid="tonk-hit-own"
                />
                <ToggleField
                    label="Drop before drawing"
                    hint="Drop at any time on your turn."
                    value={rules.can_drop_before_draw}
                    onChange={(v) => set('can_drop_before_draw', v)}
                    testid="tonk-drop-before"
                />
                <ToggleField
                    label="Can hold spreads"
                    hint="Don't have to lay a spread the turn you make it."
                    value={rules.can_hold_spreads}
                    onChange={(v) => set('can_hold_spreads', v)}
                    testid="tonk-hold-spreads"
                />
                <ToggleField
                    label="Can Tonk out mid-hand"
                    hint="Going out during play wins."
                    value={rules.can_tonk_out_mid_hand}
                    onChange={(v) => set('can_tonk_out_mid_hand', v)}
                    testid="tonk-out-midhand"
                />
                <ToggleField
                    label="Wallet-gated drop"
                    hint="Player can't drop unless wallet covers worst-case catch."
                    value={rules.wallet_gated_drop}
                    onChange={(v) => set('wallet_gated_drop', v)}
                    testid="tonk-wallet-gate"
                />
                <ToggleField
                    label="Winner starts next hand"
                    value={rules.winner_starts_next_hand}
                    onChange={(v) => set('winner_starts_next_hand', v)}
                    testid="tonk-winner-first"
                />
            </Section>

            {/* Payouts */}
            <Section title="Payouts">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <NumberField
                        label="Catch multiplier"
                        value={rules.catch_multiplier}
                        onChange={(v) => set('catch_multiplier', v)}
                        hint="Caught knocker pays this × pot to catcher. Default 2."
                        min={1} max={10}
                        testid="tonk-catch-mult"
                    />
                    <NumberField
                        label="Tonk-out multiplier"
                        value={rules.tonk_out_multiplier}
                        onChange={(v) => set('tonk_out_multiplier', v)}
                        hint="Going out pays this × pot from each opponent. Default 2."
                        min={1} max={10}
                        testid="tonk-out-mult"
                    />
                    <NumberField
                        label="Entry / pot unit ($)"
                        value={Number(rules.entry_amount)}
                        onChange={(v) => set('entry_amount', String(v))}
                        hint="Each player antes this per hand. Default $1.00."
                        min={0}
                        testid="tonk-entry"
                    />
                </div>
            </Section>

            {/* Turn timers */}
            <Section title="Turn timers">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <NumberField
                        label="Turn timer (s)"
                        value={rules.turn_timer_seconds}
                        onChange={(v) => set('turn_timer_seconds', v)}
                        hint="Default 15."
                        min={5} max={60}
                        testid="tonk-turn-timer"
                    />
                    <NumberField
                        label="Reconnect grace (s)"
                        value={rules.reconnect_grace_seconds}
                        onChange={(v) => set('reconnect_grace_seconds', v)}
                        hint="Default 45."
                        min={10} max={180}
                        testid="tonk-reconnect"
                    />
                </div>
            </Section>

            {/* Table shape */}
            <Section title="Table shape">
                <div className="grid grid-cols-3 gap-3">
                    <NumberField
                        label="Min players"
                        value={rules.min_players}
                        onChange={(v) => set('min_players', v)}
                        min={2} max={5}
                        testid="tonk-min-players"
                    />
                    <NumberField
                        label="Preferred players"
                        value={rules.preferred_players}
                        onChange={(v) => set('preferred_players', v)}
                        hint="Target for matchmaking."
                        min={2} max={5}
                        testid="tonk-pref-players"
                    />
                    <NumberField
                        label="Max players"
                        value={rules.max_players}
                        onChange={(v) => set('max_players', v)}
                        min={2} max={5}
                        testid="tonk-max-players"
                    />
                </div>
            </Section>

            {/* Bots */}
            <Section title="Bots">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs text-white/70">Default bot skill</Label>
                        <select
                            value={rules.bot_default_level || 'standard'}
                            onChange={(e) => set('bot_default_level', e.target.value)}
                            className="w-full mt-1 bg-black/40 border border-white/10 text-white rounded-md h-10 px-3 text-sm"
                            data-testid="tonk-bot-level"
                        >
                            <option value="casual">Casual</option>
                            <option value="standard">Standard</option>
                            <option value="skilled">Skilled</option>
                        </select>
                    </div>
                    <NumberField
                        label="Wait for humans (s)"
                        value={rules.bot_wait_seconds}
                        onChange={(v) => set('bot_wait_seconds', v)}
                        hint="How long the table waits for humans before a bot fills."
                        min={5} max={120}
                        testid="tonk-bot-wait"
                    />
                </div>
            </Section>

            {/* Save row */}
            <div
                className="sticky bottom-2 z-30 rounded-xl border border-[#FFD700]/40 bg-black/90 backdrop-blur p-3 flex items-center gap-3 flex-wrap"
                data-testid="tonk-settings-save-row"
            >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    {changeCount > 0 ? (
                        <>
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#FFD700]/15 text-[#FFD700] text-xs font-black px-2 py-0.5">
                                <AlertTriangle className="w-3 h-3" />
                                {changeCount} unsaved
                            </span>
                            <Input
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Reason for change (required, audit-logged)"
                                className="bg-black/40 border-white/10 text-white h-10 flex-1 min-w-[220px]"
                                data-testid="tonk-settings-reason"
                            />
                        </>
                    ) : (
                        <span className="text-xs text-white/50">All changes saved.</span>
                    )}
                </div>
                <Button
                    onClick={save}
                    disabled={busy || changeCount === 0 || !reason.trim()}
                    className="bg-[#FFD700] text-black font-black hover:bg-[#e8c500]"
                    data-testid="tonk-settings-save"
                >
                    <Save className="w-4 h-4 mr-1" /> Save changes
                </Button>
            </div>
        </div>
    );
};

export default TonkSettingsPanel;
